/* Boroj — discount rule engine.
 *
 * ONE configuration decides three things: what the customer is shown, what Shopify is asked to
 * charge, and whether those two still agree. This file is the first of those, and the reference
 * implementation of the rules; `discount_rules.py` mirrors it for the sync, and both are held
 * together by tests/fixtures/discount_rules_cases.json.
 *
 * Rule types are registered, not hardcoded: `spend_tier` is the first, and a quantity ladder,
 * a collection rule or a customer-segment rule is a new entry in TIER_KINDS + a normaliser, not a
 * rewrite. Everything below the rule boundary — scope matching, class resolution, savings maths,
 * progress — is type-agnostic.
 *
 * WHAT SHOPIFY ACTUALLY DOES (verified 2026-08-15, see docs/superpowers/specs/2026-08-15-…)
 * ---
 *  1. "The best discount or combination of discounts for the customer's cart is always applied."
 *     Two discounts that do not combine never stack; Shopify picks the one worth more MONEY, which
 *     is not the same as the bigger percentage once fixed amounts exist.
 *  2. "If the discount applies to a specific product or collection, then only these items
 *     contribute to the minimum purchase amount." The qualifying subtotal is therefore decided by
 *     the tier's SCOPE, not by a free-standing setting.
 *  3. A discount holds exactly one minimum requirement — DiscountMinimumRequirement is a union.
 *     Spend AND quantity together is not natively enforceable; see `enforceable` below.
 *  4. Automatic discounts have no usage limits. Nothing here pretends otherwise.
 *
 * The engine may only ever quote a discount Shopify will honour. Anything it cannot be sure of —
 * a segment-restricted tier, a tier outside its schedule, a tier Shopify cannot express — is
 * excluded from the quote rather than shown with a caveat.
 *
 * All money is in MINOR units (piastres). Merchant input is in EGP and converted once.
 */
(function (root, factory) {
  var api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;   // node / tests
  root.BorojDiscountRules = api;                                            // storefront
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';

  var MAX_PCT = 100;
  var OWNER_MARKER = '[BROJ]';

  /* Rule types. A new type supplies a normaliser for its tier rows; everything downstream is
     shared. `spend_tier` covers spend thresholds, quantity thresholds, or both. */
  var RULE_TYPES = { spend_tier: true };
  var DEFAULT_RULE_TYPE = 'spend_tier';

  // ─── small helpers

  function isNum(n) { return typeof n === 'number' && isFinite(n); }

  function num(v) {
    if (v === null || v === undefined || v === '') return null;
    var n = typeof v === 'number' ? v : Number(String(v).trim());
    return isFinite(n) ? n : null;
  }

  function toCents(major) {
    return Math.round(major * 100);
  }

  /** Any Shopify id shape -> the bare numeric id. "gid://shopify/Collection/7" and 7 both -> "7". */
  function bareId(v) {
    if (v === null || v === undefined) return null;
    var s = String(v).trim();
    if (!s) return null;
    var m = s.match(/(\d+)\s*$/);
    return m ? m[1] : null;
  }

  /** Accepts an array or a comma/space separated string. Returns unique numeric-string ids, sorted. */
  function idList(v) {
    if (v === null || v === undefined || v === '') return [];
    var parts = Array.isArray(v) ? v : String(v).split(/[,\s;]+/);
    var seen = {}, out = [];
    for (var i = 0; i < parts.length; i++) {
      var id = bareId(parts[i]);
      if (!id || seen[id]) continue;
      seen[id] = true;
      out.push(id);
    }
    out.sort(function (a, b) { return Number(a) - Number(b); });
    return out;
  }

  function pad(n) { return (n < 10 ? '0' : '') + n; }

  /**
   * Merchant date -> UTC ISO-8601, or null.
   *
   * Returns {value, code}: `code` is 'SCHEDULE_INVALID_DATE' when the text cannot be read at all,
   * and 'SCHEDULE_DATE_ONLY_UTC' when no zone was given. Egypt observes DST, so guessing a local
   * zone would move the boundary by an hour twice a year — the text is taken as UTC and the
   * merchant is told, rather than silently reinterpreted.
   */
  function parseDate(v) {
    if (v === null || v === undefined || String(v).trim() === '') return { value: null, code: null };
    var s = String(v).trim();
    var dOnly = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (dOnly) return { value: s + 'T00:00:00Z', code: 'SCHEDULE_DATE_ONLY_UTC' };

    var m = s.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?\s*(Z|z|[+-]\d{2}:?\d{2})?$/);
    if (!m) return { value: null, code: 'SCHEDULE_INVALID_DATE' };

    var y = +m[1], mo = +m[2], d = +m[3], hh = +m[4], mi = +m[5], ss = m[6] ? +m[6] : 0;
    var zone = m[7], offsetMin = 0, code = null;
    if (!zone) code = 'SCHEDULE_DATE_ONLY_UTC';
    else if (zone !== 'Z' && zone !== 'z') {
      var zm = zone.match(/^([+-])(\d{2}):?(\d{2})$/);
      offsetMin = (zm[1] === '-' ? -1 : 1) * (+zm[2] * 60 + +zm[3]);
    }
    var ms = Date.UTC(y, mo - 1, d, hh, mi, ss) - offsetMin * 60000;
    var out = new Date(ms);
    if (isNaN(ms)) return { value: null, code: 'SCHEDULE_INVALID_DATE' };
    return {
      value: out.getUTCFullYear() + '-' + pad(out.getUTCMonth() + 1) + '-' + pad(out.getUTCDate()) +
             'T' + pad(out.getUTCHours()) + ':' + pad(out.getUTCMinutes()) + ':' +
             pad(out.getUTCSeconds()) + 'Z',
      code: code
    };
  }

  function dateMs(iso) {
    if (!iso) return null;
    var t = Date.parse(iso);
    return isNaN(t) ? null : t;
  }

  // ─── normalisation

  function normaliseValue(raw) {
    var type = String(raw.discount_type || 'percentage').toLowerCase() === 'amount' ? 'amount' : 'percentage';
    var out = { type: type, percent: 0, amountCents: 0, eachItem: !!raw.discount_each_item };
    if (type === 'percentage') {
      var p = num(raw.discount_pct);
      if (p === null || p <= 0 || p > MAX_PCT) return null;
      out.percent = p;
    } else {
      var a = num(raw.discount_amount);
      if (a === null || a <= 0) return null;
      out.amountCents = toCents(a);
    }
    return out;
  }

  function normaliseScope(raw) {
    var mode = String(raw.scope_mode || 'all').toLowerCase();
    if (mode !== 'collections' && mode !== 'products' && mode !== 'variants') mode = 'all';
    return {
      mode: mode,
      collectionIds: idList(raw.collection_ids),
      productIds: idList(raw.product_ids),
      variantIds: idList(raw.variant_ids),
      excludeCollectionIds: idList(raw.exclude_collection_ids),
      excludeProductIds: idList(raw.exclude_product_ids),
      materialize: !!raw.scope_materialize
    };
  }

  function scopeIds(scope) {
    if (scope.mode === 'collections') return scope.collectionIds;
    if (scope.mode === 'products') return scope.productIds;
    if (scope.mode === 'variants') return scope.variantIds;
    return [];
  }

  function hasExclusions(scope) {
    return scope.excludeCollectionIds.length > 0 || scope.excludeProductIds.length > 0;
  }

  /**
   * What the merchant said about combining, resolved to the three booleans Shopify holds.
   *
   *   none            refuse every class
   *   selected        exactly the switches the merchant ticked
   *   all             every class Shopify allows
   *
   * The tier's OWN class is stripped afterwards, in normaliseRule/normaliseRules — "combine with
   * everything" can never mean "combine with the other rungs of this ladder", because that is the
   * one thing the whole design promises will not happen.
   */
  function resolveCombines(raw) {
    var mode = String(raw.combination_mode || 'selected').toLowerCase();
    if (mode !== 'none' && mode !== 'all') mode = 'selected';
    if (mode === 'none') return { mode: mode, combines: { product: false, order: false, shipping: false } };
    if (mode === 'all') return { mode: mode, combines: { product: true, order: true, shipping: true } };
    return {
      mode: mode,
      combines: {
        product: !!raw.combines_product,
        order: !!raw.combines_order,
        shipping: raw.combines_shipping === undefined ? true : !!raw.combines_shipping
      }
    };
  }

  function normaliseEligibility(raw) {
    var mode = String(raw.eligibility || 'all').toLowerCase();
    if (mode !== 'segments' && mode !== 'customers') mode = 'all';
    return { mode: mode, segmentIds: idList(raw.segment_ids), customerIds: idList(raw.customer_ids) };
  }

  function eligibilityIds(e) {
    return e.mode === 'segments' ? e.segmentIds : (e.mode === 'customers' ? e.customerIds : []);
  }

  /** One merchant row -> one canonical tier, or null when the row is unusable. */
  function normaliseTier(raw, ruleKey) {
    if (!raw || typeof raw !== 'object') return null;

    var value = normaliseValue(raw);
    if (!value) return null;

    var spendGiven = !(raw.min_spend === undefined || raw.min_spend === null || raw.min_spend === '');
    var spendMajor = spendGiven ? num(raw.min_spend) : 0;
    if (spendMajor === null || spendMajor < 0) return null;

    var qtyGiven = !(raw.min_qty === undefined || raw.min_qty === null || raw.min_qty === '');
    var qty = qtyGiven ? num(raw.min_qty) : 0;
    if (qty === null || qty < 0) qty = 0;
    qty = Math.floor(qty);

    /* A row with no threshold at all is an unfinished row, not "discount everything": a merchant who
       clears the spend field must not silently hand every order a discount. An explicit 0 is kept,
       because that is a deliberate "no minimum". */
    if (!spendGiven && !qtyGiven) return null;

    var minSpendCents = toCents(spendMajor);

    var policy = String(raw.min_qty_policy || 'strict').toLowerCase() === 'display_only'
      ? 'display_only' : 'strict';
    var scope = normaliseScope(raw);
    var eligibility = normaliseEligibility(raw);

    var starts = parseDate(raw.starts_at);
    var ends = parseDate(raw.ends_at);

    var cls = (scope.mode === 'all' && !(hasExclusions(scope) && scope.materialize)) ? 'ORDER' : 'PRODUCT';

    /* Condition mode.
     *
     * With one threshold there is nothing to combine, so the mode is 'single' whatever was asked.
     *
     * OR is the interesting case, and it IS expressible: two automatic discounts of equal value and
     * identical scope — one with the spend minimum, one with the quantity minimum. They land in the
     * same discount class with own-class combining off, so Shopify applies the better of the two,
     * which at equal value is simply "whichever condition the customer met". Exact in both
     * directions, at the cost of two of the store's 25 automatic discounts.
     *
     * AND is not expressible: DiscountMinimumRequirement is a union, so one discount holds exactly
     * one minimum. Either the tier is left unsynced, or the merchant accepts that Shopify enforces
     * the spend while the storefront enforces the item count — which can only under-promise.
     */
    var bothThresholds = minSpendCents > 0 && qty > 0;
    var requested = String(raw.condition_mode || 'and').toLowerCase() === 'or' ? 'or' : 'and';
    var conditionMode = bothThresholds ? requested : 'single';

    var displayOnly = conditionMode === 'and' && policy === 'display_only' ? ['minQty'] : [];
    var enforceable = true;
    var syncShape;
    if (conditionMode === 'or') {
      syncShape = ['s', 'q'];
    } else if (conditionMode === 'and') {
      if (policy === 'display_only') {
        syncShape = ['s'];
      } else {
        enforceable = false;
        syncShape = [];
      }
    } else {
      syncShape = minSpendCents > 0 ? ['s'] : (qty > 0 ? ['q'] : []);
    }
    if (hasExclusions(scope) && !scope.materialize) {
      enforceable = false;
      syncShape = [];
    }

    var combining = resolveCombines(raw);

    return {
      id: minSpendCents > 0 ? ruleKey + ':' + minSpendCents
                            : (qty > 0 ? ruleKey + ':q' + qty : ruleKey + ':0'),
      ruleKey: ruleKey,
      minSpendCents: minSpendCents,
      minQty: qty,
      minQtyPolicy: policy,
      conditionMode: conditionMode,
      value: value,
      scope: scope,
      schedule: { startsAt: starts.value, endsAt: ends.value },
      eligibility: eligibility,
      combinationMode: combining.mode,
      combines: combining.combines,
      label: raw.label == null ? '' : String(raw.label),
      class: cls,
      restricted: eligibility.mode !== 'all',
      enforceable: enforceable,
      displayOnly: displayOnly,
      syncShape: syncShape
    };
  }

  /** A tier's scope, as a string, so tiers can be grouped by "the same catalogue". */
  function scopeSignature(scope) {
    return scope.mode + '(' + scopeIds(scope).join(',') + ')|x(' +
           scope.excludeCollectionIds.concat(scope.excludeProductIds).join(',') + ')' +
           (scope.materialize ? '!' : '');
  }

  /**
   * Raw rule -> canonical rule.
   *
   * Tiers are sorted by threshold; on a tie within one scope the larger discount survives, because
   * Shopify would apply it anyway and showing the smaller one would understate.
   */
  function normaliseRule(raw) {
    raw = raw || {};
    var key = String(raw.key || 'spend');
    var type = RULE_TYPES[raw.type] ? raw.type : DEFAULT_RULE_TYPE;
    var enabled = raw.enabled !== false;
    /* 'scope' is the default because it is the only basis that always matches Shopify: the
       qualifying subtotal is the cart lines the discount applies to, which for an all-products tier
       is the whole cart. 'bundle' and 'cart' are kept as deliberate merchant choices. */
    var basis = String(raw.basis || 'scope').toLowerCase();
    if (basis !== 'cart' && basis !== 'bundle') basis = 'scope';

    var tiers = [];
    if (enabled) {
      var rows = raw.tiers || [];
      for (var i = 0; i < rows.length; i++) {
        var t = normaliseTier(rows[i], key);
        if (t) tiers.push(t);
      }
      tiers.sort(function (a, b) {
        return a.minSpendCents - b.minSpendCents ||
               a.minQty - b.minQty ||
               valueWeight(b) - valueWeight(a) ||
               (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);
      });
      // one tier per (threshold, scope): the first — i.e. the most valuable — wins
      var seen = {}, dedup = [];
      for (var j = 0; j < tiers.length; j++) {
        var k = tiers[j].id + '@' + scopeSignature(tiers[j].scope);
        if (seen[k]) continue;
        seen[k] = true;
        dedup.push(tiers[j]);
      }
      tiers = dedup;

      // Tiers of one rule must never stack with each other, so every class the rule occupies is
      // forced off. A ladder that mixes scopes occupies both merchandise classes and loses both.
      var classes = {};
      for (var c = 0; c < tiers.length; c++) classes[tiers[c].class] = true;
      for (var d = 0; d < tiers.length; d++) {
        if (classes.ORDER) tiers[d].combines.order = false;
        if (classes.PRODUCT) tiers[d].combines.product = false;
      }
    }

    return { key: key, type: type, enabled: enabled, basis: basis, tiers: tiers };
  }

  /** Only used to break a threshold tie: the bigger headline number wins. */
  function valueWeight(tier) {
    return tier.value.type === 'percentage' ? tier.value.percent : tier.value.amountCents;
  }

  /**
   * All the rules together — and one thing that can only be decided together.
   *
   * Shopify does allow two order discounts to combine when both sides agree, so "combine with
   * everything" on two different rules would genuinely stack at checkout while this panel showed
   * one. Every merchandise class occupied by ANY engine-owned tier is therefore switched off across
   * ALL of them. What the merchant is choosing is whether to combine with discounts this system
   * does not own — which is the only combination it can honestly predict.
   */
  function normaliseRules(list) {
    var out = [], i, j;
    for (i = 0; i < (list || []).length; i++) out.push(normaliseRule(list[i]));

    var classes = {};
    for (i = 0; i < out.length; i++) {
      if (!out[i].enabled) continue;
      for (j = 0; j < out[i].tiers.length; j++) classes[out[i].tiers[j].class] = true;
    }
    for (i = 0; i < out.length; i++) {
      for (j = 0; j < out[i].tiers.length; j++) {
        if (classes.ORDER) out[i].tiers[j].combines.order = false;
        if (classes.PRODUCT) out[i].tiers[j].combines.product = false;
      }
    }
    return out;
  }

  // ─── validation

  function err(code, message) { return { code: code, severity: 'error', message: message }; }
  function warn(code, message) { return { code: code, severity: 'warning', message: message }; }

  /**
   * Merchant-facing validation, run against the RAW rows so junk can be reported rather than
   * silently dropped. `ctx.externalMerchandiseDiscounts` is the list of discount titles the store
   * owns but this engine does not — enabling a merchandise combination against those means the
   * sidebar can no longer predict the total.
   */
  function validateRule(raw, ctx) {
    ctx = ctx || {};
    var out = [];
    raw = raw || {};
    if (raw.enabled === false) return out;

    var rows = raw.tiers || [];
    if (!rows.length) {
      out.push(err('RULE_NO_TIERS', 'the rule is enabled but has no tiers'));
      return out;
    }

    var basis = String(raw.basis || 'scope').toLowerCase();
    var seenThreshold = {};

    if (basis === 'bundle') {
      out.push(warn('BASIS_BUNDLE_UNDERSTATES', 'the rule qualifies on the bundle alone, but Shopify ' +
        'measures the whole cart (or the scoped part of it). A customer who already has qualifying ' +
        'items in the cart will reach the tier sooner than this panel shows — the panel can only ' +
        'ever understate, never promise a discount Shopify refuses'));
    }

    for (var i = 0; i < rows.length; i++) {
      var t = rows[i] || {};
      var where = 'tier ' + (i + 1);

      var spend = t.min_spend === undefined || t.min_spend === null || t.min_spend === '' ? 0 : num(t.min_spend);
      if (spend === null || spend < 0) {
        out.push(err('TIER_SPEND_INVALID', where + ': minimum spend must be a number of 0 or more'));
        spend = null;
      }

      var type = String(t.discount_type || 'percentage').toLowerCase() === 'amount' ? 'amount' : 'percentage';
      if (type === 'percentage') {
        var p = num(t.discount_pct);
        if (p === null || p <= 0) out.push(err('TIER_NO_VALUE', where + ': the discount must be greater than 0%'));
        else if (p > MAX_PCT) out.push(err('TIER_PERCENT_RANGE', where + ': a discount cannot exceed ' + MAX_PCT + '%'));
      } else {
        var a = num(t.discount_amount);
        if (a === null || a <= 0) out.push(err('TIER_NO_VALUE', where + ': the discount amount must be greater than 0'));
      }

      var qty = num(t.min_qty) || 0;
      var policy = String(t.min_qty_policy || 'strict').toLowerCase();
      var mode = String(t.condition_mode || 'and').toLowerCase() === 'or' ? 'or' : 'and';

      if (mode === 'or' && !(spend !== null && spend > 0 && qty > 0)) {
        out.push(warn('TIER_MODE_OR_SINGLE', where + ': "either condition" needs both a spend and an ' +
          'item minimum. With only one of them the tier behaves as a plain threshold'));
      }
      if (mode === 'or' && spend !== null && spend > 0 && qty > 0) {
        out.push(warn('TIER_OR_COSTS_TWO_DISCOUNTS', where + ': "either condition" is created as TWO ' +
          'Shopify discounts of equal value — one for the spend, one for the item count. Shopify ' +
          'applies the better of the two, so the customer still receives the tier exactly once, but ' +
          'the tier uses two of the store’s 25 automatic discounts'));
      }
      if (mode === 'and' && spend !== null && spend > 0 && qty > 0) {
        if (policy === 'display_only') {
          out.push(warn('TIER_MIN_QTY_DISPLAY_ONLY', where + ': Shopify will enforce the ' + spend +
            ' spend only; the ' + qty + '-item minimum is enforced by the bundle builder alone, so a ' +
            'customer who meets the spend with fewer items may still receive the discount at checkout'));
        } else {
          out.push(err('TIER_MIN_QTY_UNSUPPORTED', where + ': Shopify holds exactly one minimum ' +
            'requirement per discount, so "spend ' + spend + ' AND ' + qty + ' items" cannot be created. ' +
            'Drop one, or set the minimum-quantity policy to "display only" and accept that Shopify ' +
            'enforces the spend alone'));
        }
      }

      var scope = normaliseScope(t);
      if (scope.mode !== 'all' && scopeIds(scope).length === 0) {
        out.push(err('TIER_SCOPE_EMPTY', where + ': the scope is set to ' + scope.mode +
          ' but nothing is selected'));
      }
      if (hasExclusions(scope) && !scope.materialize) {
        out.push(err('TIER_EXCLUSIONS_NOT_MATERIALIZED', where + ': Shopify has no "everything except" ' +
          'discount. Tick "resolve the scope to a product list" so the exclusions can be applied, or ' +
          'remove them'));
      }
      if (basis === 'cart' && scope.mode !== 'all') {
        out.push(err('BASIS_CART_REQUIRES_ALL_SCOPE', where + ': the rule qualifies on the whole cart ' +
          'but the tier is limited to ' + scope.mode + '. Shopify counts only the items the discount ' +
          'applies to, so the two would disagree'));
      }

      var starts = parseDate(t.starts_at), ends = parseDate(t.ends_at);
      if (starts.code === 'SCHEDULE_INVALID_DATE') out.push(err('SCHEDULE_INVALID_DATE', where + ': the start date cannot be read'));
      if (ends.code === 'SCHEDULE_INVALID_DATE') out.push(err('SCHEDULE_INVALID_DATE', where + ': the end date cannot be read'));
      if (starts.code === 'SCHEDULE_DATE_ONLY_UTC' || ends.code === 'SCHEDULE_DATE_ONLY_UTC') {
        out.push(warn('SCHEDULE_DATE_ONLY_UTC', where + ': a date with no time zone is taken as UTC ' +
          '(Cairo is 2–3 hours ahead depending on daylight saving) — add an offset such as ' +
          '2026-09-01T00:00:00+02:00 to be exact'));
      }
      if (starts.value && ends.value && dateMs(ends.value) <= dateMs(starts.value)) {
        out.push(err('SCHEDULE_ENDS_BEFORE_STARTS', where + ': the end date is not after the start date'));
      }

      var elig = normaliseEligibility(t);
      if (elig.mode !== 'all' && eligibilityIds(elig).length === 0) {
        out.push(err('ELIGIBILITY_IDS_MISSING', where + ': eligibility is limited to ' + elig.mode +
          ' but none are listed'));
      }

      if (spend !== null && (spend > 0 || qty > 0)) {
        var tk = (spend > 0 ? 's' + toCents(spend) : 'q' + qty) + '@' + scopeSignature(scope);
        if (seenThreshold[tk]) {
          out.push(err('TIER_DUPLICATE_THRESHOLD', where + ': another tier already uses this threshold ' +
            'with the same scope'));
        }
        seenThreshold[tk] = true;
      }

      var wantsProduct = !!t.combines_product, wantsOrder = !!t.combines_order;
      var external = ctx.externalMerchandiseDiscounts || [];
      if ((wantsProduct || wantsOrder) && external.length) {
        out.push(warn('COMBINES_MERCHANDISE_RISK', where + ': combining with other merchandise ' +
          'discounts lets this tier stack on top of ' + external.join(', ') + '. The bundle builder ' +
          'cannot predict discounts it does not own, so the total it shows becomes an estimate'));
      }
    }

    // A tier that can never fire is dead configuration, not a preference: within one scope a higher
    // threshold must be worth more, or Shopify's "best discount" rule will always pick the lower one.
    var canon = normaliseRule(raw);
    var byScope = {};
    for (var s = 0; s < canon.tiers.length; s++) {
      var sig = scopeSignature(canon.tiers[s].scope);
      (byScope[sig] = byScope[sig] || []).push(canon.tiers[s]);
    }
    for (var key in byScope) {
      if (!Object.prototype.hasOwnProperty.call(byScope, key)) continue;
      var ladder = byScope[key];
      for (var n = 1; n < ladder.length; n++) {
        var lo = ladder[n - 1], hi = ladder[n];
        if (lo.value.type !== hi.value.type) continue;       // money vs percent is not comparable here
        if (valueWeight(hi) <= valueWeight(lo)) {
          out.push(err('TIER_LADDER_NOT_MONOTONIC', 'the tier at ' + thresholdLabel(hi) + ' is not worth ' +
            'more than the tier at ' + thresholdLabel(lo) + ' — Shopify always applies the best ' +
            'discount, so the higher tier could never be used'));
        }
      }
    }
    return out;
  }

  function thresholdLabel(tier) {
    if (tier.minSpendCents > 0) return String(tier.minSpendCents / 100);
    return tier.minQty + ' items';
  }

  function validateRules(list, ctx) {
    var out = [];
    for (var i = 0; i < (list || []).length; i++) {
      var errs = validateRule(list[i], ctx);
      for (var j = 0; j < errs.length; j++) {
        errs[j].ruleKey = (list[i] || {}).key || 'spend';
        out.push(errs[j]);
      }
    }
    return out;
  }

  function errorsOnly(list) {
    return (list || []).filter(function (e) { return e.severity === 'error'; });
  }

  // ─── signatures

  /**
   * A tier's synced identity, as a plain string.
   *
   * The sync stores this in a shop metafield and the section rebuilds it in Liquid, which cannot
   * hash — so it is a joined string, not a digest. Every field the sync writes to Shopify appears
   * here, and nothing else, so "the string changed" means exactly "Shopify is out of date".
   */
  function tierSignature(tier) {
    var v = tier.value.type === 'percentage'
      ? 'pct:' + tier.value.percent
      : 'amt:' + tier.value.amountCents + (tier.value.eachItem ? ':each' : '');
    return [
      tier.id,
      tier.class,
      v,
      's' + tier.minSpendCents,
      'q' + tier.minQty + tier.minQtyPolicy,
      tier.conditionMode,
      scopeSignature(tier.scope),
      (tier.schedule.startsAt || '-') + '..' + (tier.schedule.endsAt || '-'),
      tier.eligibility.mode + '(' + eligibilityIds(tier.eligibility).join(',') + ')',
      'c' + (tier.combines.product ? 1 : 0) + (tier.combines.order ? 1 : 0) + (tier.combines.shipping ? 1 : 0)
    ].join('|');
  }

  function ruleSignature(rule) {
    var canon = rule && rule.tiers && rule.tiers.length && rule.tiers[0].id ? rule : normaliseRule(rule);
    return canon.tiers.map(tierSignature).join(';');
  }

  function configSignature(rules) {
    // normaliseRules, not normaliseRule: cross-rule combination forcing changes what Shopify is sent
    var canon = normaliseRules(rules).filter(function (r) { return r.enabled && r.tiers.length; });
    canon.sort(function (a, b) { return a.key < b.key ? -1 : a.key > b.key ? 1 : 0; });
    return canon.map(ruleSignature).join('#');
  }

  // ─── scope matching

  function lineInScope(line, scope) {
    var cols = line.collectionIds || [];
    var i;
    for (i = 0; i < scope.excludeProductIds.length; i++) {
      if (String(line.productId) === scope.excludeProductIds[i]) return false;
    }
    for (i = 0; i < scope.excludeCollectionIds.length; i++) {
      if (cols.indexOf(scope.excludeCollectionIds[i]) !== -1) return false;
    }
    if (scope.mode === 'all') return true;
    if (scope.mode === 'products') return scope.productIds.indexOf(String(line.productId)) !== -1;
    if (scope.mode === 'variants') return scope.variantIds.indexOf(String(line.variantId)) !== -1;
    for (i = 0; i < scope.collectionIds.length; i++) {
      if (cols.indexOf(scope.collectionIds[i]) !== -1) return true;
    }
    return false;
  }

  function totals(lines) {
    var base = 0, qty = 0;
    for (var i = 0; i < lines.length; i++) {
      var q = isNum(lines[i].qty) ? lines[i].qty : 1;
      var p = isNum(lines[i].priceCents) ? lines[i].priceCents : 0;
      base += p * q;
      qty += q;
    }
    return { baseCents: base, qty: qty };
  }

  function filterScope(lines, scope) {
    var out = [];
    for (var i = 0; i < lines.length; i++) if (lineInScope(lines[i], scope)) out.push(lines[i]);
    return out;
  }

  // ─── savings

  /** What a tier is worth, in money, on a given base. Fixed amounts never exceed the base. */
  function savingsOn(tier, baseCents, qty) {
    if (baseCents <= 0) return 0;
    if (tier.value.type === 'percentage') return Math.round(baseCents * tier.value.percent / 100);
    var amount = tier.value.eachItem ? tier.value.amountCents * qty : tier.value.amountCents;
    return Math.min(amount, baseCents);
  }

  // ─── evaluation

  /**
   * Two clocks, on purpose.
   *
   * `nowMs` is the server's time, and it alone decides whether a tier has STARTED — a device clock
   * running fast must not open a promotion Shopify has not begun. `endMs` is the later of the
   * server's time and the visitor's, and it decides whether a tier has ENDED: if either clock says
   * the promotion is over, it is over. That asymmetry is deliberate — a page restored from the
   * browser's back-forward cache carries a stale server time, and the wrong failure is the one that
   * keeps quoting an expired discount.
   */
  function inSchedule(tier, nowMs, endMs) {
    if (nowMs === null || nowMs === undefined) return true;    // no clock supplied: no filtering
    var s = dateMs(tier.schedule.startsAt), e = dateMs(tier.schedule.endsAt);
    if (s !== null && nowMs < s) return false;
    var endClock = (endMs === null || endMs === undefined) ? nowMs : Math.max(nowMs, endMs);
    if (e !== null && endClock >= e) return false;
    return true;
  }

  /** Tiers the widget is allowed to talk about at all. */
  function advertisable(tier, nowMs, showRestricted, endMs) {
    if (!tier.enforceable) return false;                       // Shopify could not be made to do it
    if (tier.restricted && !showRestricted) return false;      // we cannot know the visitor's segment
    return inSchedule(tier, nowMs, endMs);
  }

  function candidateFor(tier, lines, bundleLines) {
    var scoped = filterScope(lines, tier.scope);
    var t = totals(scoped);
    var bt = totals(filterScope(bundleLines, tier.scope));

    var meetsSpend = tier.minSpendCents <= 0 || t.baseCents >= tier.minSpendCents;
    var meetsQty = tier.minQty <= 0 || t.qty >= tier.minQty;
    var qualifies = tier.conditionMode === 'or' ? (meetsSpend || meetsQty) : (meetsSpend && meetsQty);
    if (!qualifies) return null;

    return {
      tierId: tier.id,
      ruleKey: tier.ruleKey,
      source: tier.ruleKey,
      tier: tier,
      class: tier.class,
      combines: tier.combines,
      baseCents: t.baseCents,
      qty: t.qty,
      bundleBaseCents: bt.baseCents,
      bundleQty: bt.qty,
      savingsCents: savingsOn(tier, t.baseCents, t.qty)
    };
  }

  /**
   * Every automatic discount the store has that this system does NOT own, mirrored so the widget
   * knows which discount really wins.
   *
   * Both shapes are accepted: `qtyTiers` (the original quantity-only mirror) and `discounts` (any
   * live automatic discount — a spend minimum, a quantity minimum, a percentage or a fixed amount,
   * scoped or not). The general form matters: a merchant who adds "15% off orders over 1,000" in the
   * Shopify admin has changed what the customer is charged, and a panel that only watched quantity
   * discounts would carry on quoting a number the checkout no longer honours.
   *
   * `combinesWith` and `class` are carried from what Shopify actually reports, not assumed: whether
   * one of these can stack on an engine tier is the merchant's setting on THEIR discount, and
   * guessing it either overstates the total or hides a saving the customer will get.
   */
  function externalCandidates(external, lines, bundleLines) {
    var out = [];
    var list = [];
    if (external && external.qtyTiers) list = list.concat(external.qtyTiers);
    if (external && external.discounts) list = list.concat(external.discounts);

    for (var i = 0; i < list.length; i++) {
      var raw = list[i] || {};
      var minQty = num(raw.minQty) || 0;
      var minSpendCents = raw.minSpendCents != null ? (num(raw.minSpendCents) || 0)
        : (raw.minSpend != null ? toCents(num(raw.minSpend) || 0) : 0);

      var pct = num(raw.percent);
      var amountCents = raw.amountCents != null ? (num(raw.amountCents) || 0)
        : (raw.amount != null ? toCents(num(raw.amount) || 0) : 0);
      var value;
      if (pct !== null && pct > 0) {
        value = { type: 'percentage', percent: pct, amountCents: 0, eachItem: !!raw.eachItem };
      } else if (amountCents > 0) {
        value = { type: 'amount', percent: 0, amountCents: amountCents, eachItem: !!raw.eachItem };
      } else {
        continue;                                     // nothing to award: not a discount we can model
      }

      var xc = raw.combinesWith || {};
      var cols = raw.collectionIds || [];
      var scope = normaliseScope({
        scope_mode: cols.length ? 'collections' : 'all',
        collection_ids: cols
      });
      /* Shopify decides the class from the scope, and reports it. Trust what it reported; fall back
         to the same derivation the engine uses for its own tiers. */
      var cls = raw.class === 'ORDER' || raw.class === 'PRODUCT' ? raw.class
        : (cols.length ? 'PRODUCT' : 'ORDER');

      var scoped = filterScope(lines, scope), t = totals(scoped);
      // a foreign discount holds one minimum, so both tests are simply "met or not applicable"
      if (minQty > 0 && t.qty < minQty) continue;
      if (minSpendCents > 0 && t.baseCents < minSpendCents) continue;
      if (t.baseCents <= 0) continue;

      var bt = totals(filterScope(bundleLines, scope));
      var id = 'external:' + (minSpendCents > 0 ? 's' + minSpendCents : 'q' + minQty);
      var tier = {
        id: id, ruleKey: 'external', minSpendCents: minSpendCents, minQty: minQty,
        value: value,
        scope: scope, class: cls, restricted: false, enforceable: true, displayOnly: [],
        schedule: { startsAt: null, endsAt: null },
        conditionMode: 'single',
        combines: { product: !!xc.product, order: !!xc.order, shipping: !!xc.shipping },
        label: raw.title || raw.label || ''
      };
      out.push({
        tierId: tier.id, ruleKey: 'external', source: 'external', tier: tier, class: cls,
        combines: tier.combines, baseCents: t.baseCents, qty: t.qty,
        bundleBaseCents: bt.baseCents, bundleQty: bt.qty,
        savingsCents: savingsOn(tier, t.baseCents, t.qty)
      });
    }
    return out;
  }

  function mayCombine(a, b) {
    if (a.class === b.class) return false;                    // one discount per class, always
    var aAllows = b.class === 'PRODUCT' ? a.combines.product : b.class === 'ORDER' ? a.combines.order : a.combines.shipping;
    var bAllows = a.class === 'PRODUCT' ? b.combines.product : a.class === 'ORDER' ? b.combines.order : b.combines.shipping;
    return !!(aAllows && bAllows);
  }

  /**
   * Two combined discounts are applied in sequence — the product discount reduces the line, then
   * the order discount is taken off what is left. Adding the two percentages would quote a saving
   * larger than the order will actually receive, and this panel must never be the optimistic one.
   */
  function combinedSavings(pair) {
    var product = pair[0].class === 'PRODUCT' ? pair[0] : pair[1];
    var order = product === pair[0] ? pair[1] : pair[0];
    var first = product.savingsCents;
    var second = savingsOn(order.tier, Math.max(0, order.baseCents - first), order.qty);
    return first + second;
  }

  function bundleShare(candidate, savings) {
    if (candidate.baseCents <= 0) return 0;
    if (candidate.bundleBaseCents >= candidate.baseCents) return savings;
    return Math.round(savings * candidate.bundleBaseCents / candidate.baseCents);
  }

  /**
   * The whole picture, in one call.
   *
   * `savingsCents` is what this panel may show — the part of the discount that belongs to the
   * bundle. `orderSavingsCents` is what the order will receive, which is larger whenever the
   * customer already has qualifying items in the cart.
   */
  /** Every tier that currently qualifies, plus the merchant's own discounts, on one basket. */
  function collectCandidates(rules, cartLines, bundleLines, nowMs, showRestricted, external, endMs) {
    var candidates = [];
    for (var r = 0; r < rules.length; r++) {
      var rule = rules[r];
      if (!rule.enabled) continue;
      var lines = rule.basis === 'bundle' ? bundleLines : cartLines.concat(bundleLines);
      for (var t = 0; t < rule.tiers.length; t++) {
        var tier = rule.tiers[t];
        if (!advertisable(tier, nowMs, showRestricted, endMs)) continue;
        var c = candidateFor(tier, lines, bundleLines);
        if (c) { c.basis = rule.basis; candidates.push(c); }
      }
    }
    return candidates.concat(externalCandidates(external, cartLines.concat(bundleLines), bundleLines));
  }

  /**
   * Shopify applies "the best discount or combination of discounts for the customer's cart".
   * Candidates are few, so every legal set is enumerated outright rather than reasoned about.
   */
  function resolve(candidates) {
    var best = { total: 0, set: [] }, i, j;
    for (i = 0; i < candidates.length; i++) {
      if (candidates[i].savingsCents > best.total) best = { total: candidates[i].savingsCents, set: [candidates[i]] };
    }
    for (i = 0; i < candidates.length; i++) {
      for (j = i + 1; j < candidates.length; j++) {
        if (!mayCombine(candidates[i], candidates[j])) continue;
        var pair = [candidates[i], candidates[j]];
        var total = combinedSavings(pair);
        if (total > best.total) best = { total: total, set: pair };
      }
    }
    return best;
  }

  function evaluate(input) {
    input = input || {};
    var rules = normaliseRules(input.rules);
    var cartLines = (input.cart && input.cart.lines) || [];
    var bundleLines = input.bundle || [];
    var nowMs = input.now === undefined || input.now === null ? null : dateMs(input.now);
    var showRestricted = !!input.showRestricted;
    var external = input.external || null;
    /* the visitor's own clock, used only to close a tier early — never to open one */
    var endMs = input.clientNow === undefined || input.clientNow === null ? null : dateMs(input.clientNow);

    var candidates = collectCandidates(rules, cartLines, bundleLines, nowMs, showRestricted, external, endMs);
    var best = resolve(candidates);

    var applied = best.set;
    var i;
    var orderSavings = best.total;

    /* Split the win back out to the panel. Each applied discount is credited with the share of its
       own saving that comes from bundle lines; a combined pair is split the same way it was costed,
       product discount first. */
    var bundleSavings = 0;
    if (applied.length === 1) {
      bundleSavings = bundleShare(applied[0], orderSavings);
    } else if (applied.length > 1) {
      var productPart = appliedProductSavings(applied);
      for (i = 0; i < applied.length; i++) {
        var own = applied[i].class === 'PRODUCT' ? productPart : orderSavings - productPart;
        bundleSavings += bundleShare(applied[i], own);
      }
    }
    var bundleTotals = totals(bundleLines);
    if (bundleSavings > bundleTotals.baseCents) bundleSavings = bundleTotals.baseCents;

    var winner = applied.length === 1 ? applied[0] : null;
    var percent = winner && winner.tier.value.type === 'percentage' ? winner.tier.value.percent : 0;

    var summary = {
      percent: percent,
      valueType: winner ? winner.tier.value.type : (applied.length ? 'mixed' : null),
      source: applied.length === 0 ? 'none' : (applied.length > 1 ? 'combined' : winner.source),
      combined: applied.length > 1,
      tierId: winner ? winner.tierId : null,
      tier: winner ? winner.tier : null,
      baseCents: winner ? winner.baseCents : (applied.length ? applied[0].baseCents : 0),
      orderSavingsCents: orderSavings,
      savingsCents: bundleSavings,
      bundleSavingsCents: bundleSavings,
      subtotalCents: bundleTotals.baseCents,
      totalCents: bundleTotals.baseCents - bundleSavings,
      itemCount: bundleTotals.qty,
      applied: applied,
      next: null,
      atTop: false
    };
    summary.next = nextTier(rules, cartLines, bundleLines, nowMs, showRestricted, orderSavings, external, endMs);

    /* "Top tier reached" may only be said when it is true. There is a second reason `next` comes
       back empty: a better tier exists but something the customer would get anyway — usually the
       quantity ladder — would pay more at that basket, so naming it would be a lie. In that case
       the ladder simply says nothing. */
    if (winner && !summary.next && winner.tier.ruleKey !== 'external') {
      var higher = false;
      for (i = 0; i < rules.length && !higher; i++) {
        if (!rules[i].enabled) continue;
        for (var h = 0; h < rules[i].tiers.length; h++) {
          var cand = rules[i].tiers[h];
          if (cand.ruleKey !== winner.tier.ruleKey) continue;
          if (!advertisable(cand, nowMs, showRestricted, endMs)) continue;
          if (cand.minSpendCents > winner.tier.minSpendCents || cand.minQty > winner.tier.minQty) {
            higher = true;
            break;
          }
        }
      }
      summary.atTop = !higher;
    }
    return summary;
  }

  function appliedProductSavings(applied) {
    for (var i = 0; i < applied.length; i++) if (applied[i].class === 'PRODUCT') return applied[i].savingsCents;
    return 0;
  }

  /**
   * The next tier worth naming, and what it would take to reach it.
   *
   * A tier is only worth naming if reaching it would beat what the customer already has — which may
   * come from a rule they are not looking at, or from the merchant's own quantity ladder. Promising
   * "spend 200 more for 10% off" to someone already holding 25% is a promise Shopify would refuse to
   * keep, because it keeps applying the better discount.
   */
  function nextTier(rules, cartLines, bundleLines, nowMs, showRestricted, currentSavings, external, endMs) {
    var best = null;
    for (var r = 0; r < rules.length; r++) {
      var rule = rules[r];
      if (!rule.enabled) continue;
      var lines = rule.basis === 'bundle' ? bundleLines : cartLines.concat(bundleLines);
      for (var t = 0; t < rule.tiers.length; t++) {
        var tier = rule.tiers[t];
        if (!advertisable(tier, nowMs, showRestricted, endMs)) continue;

        var scoped = filterScope(lines, tier.scope), tot = totals(scoped);
        var gapCents = Math.max(0, tier.minSpendCents - tot.baseCents);
        var gapQty = Math.max(0, tier.minQty - tot.qty);
        if (gapCents === 0 && gapQty === 0) continue;            // already held, or already counted

        /* An OR tier offers two ways in, and they are costed separately: spending more, or adding
           an item. Either one alone unlocks it, so advertising the pair as if both were required
           would overstate the effort. An AND tier has a single way in and is costed as one. */
        var paths = [];
        if (tier.conditionMode === 'or') {
          if (gapCents > 0) paths.push({ cents: gapCents, qty: 0 });
          if (gapQty > 0) paths.push({ cents: 0, qty: gapQty });
        } else {
          paths.push({ cents: gapCents, qty: gapQty });
        }

        /* Cost the whole basket as it WOULD be, not just this tier in isolation. The competing
           discounts grow with the basket too — a quantity ladder that already beats this tier will
           usually still beat it once the customer has spent more, and telling them otherwise is a
           promise Shopify would refuse to keep. */
        var wonCents = -1, wonQty = -1, bestProjected = 0;
        for (var p = 0; p < paths.length; p++) {
          var projectedLines = bundleLines.concat(
            [syntheticLine(tier, paths[p].cents, paths[p].qty, bundleLines)]);
          var projected = resolve(collectCandidates(rules, cartLines, projectedLines, nowMs,
            showRestricted, external, endMs));
          if (projected.total <= currentSavings) continue;       // no better off: never advertise
          if (!setContains(projected.set, tier.id)) continue;    // something else would win anyway
          if (paths[p].cents > 0 && (wonCents < 0 || paths[p].cents < wonCents)) wonCents = paths[p].cents;
          if (paths[p].qty > 0 && (wonQty < 0 || paths[p].qty < wonQty)) wonQty = paths[p].qty;
          if (projected.total > bestProjected) bestProjected = projected.total;
        }
        if (wonCents < 0 && wonQty < 0) continue;                // no way in that would actually pay

        var cand = {
          tierId: tier.id, tier: tier,
          remainingCents: wonCents < 0 ? 0 : wonCents,
          remainingQty: wonQty < 0 ? 0 : wonQty,
          projectedSavingsCents: bestProjected
        };
        if (!best ||
            cand.remainingCents < best.remainingCents ||
            (cand.remainingCents === best.remainingCents && cand.projectedSavingsCents > best.projectedSavingsCents)) {
          best = cand;
        }
      }
    }
    return best;
  }

  function setContains(set, tierId) {
    for (var i = 0; i < set.length; i++) if (set[i].tierId === tierId) return true;
    return false;
  }

  /**
   * The basket addition that would satisfy a tier, used only to cost the projection.
   *
   * It inherits the collections already represented in the bundle, because the only things this
   * widget lets a customer add are more of the same kind of product — and a competing discount
   * scoped to those collections would grow with the basket too. Modelling the addition as
   * collection-less would quietly assume the competitor stands still, which is how a panel ends up
   * advertising a tier the customer will never actually be given.
   */
  function syntheticLine(tier, needCents, needQty, contextLines) {
    var scope = tier.scope;
    var pick = function (ids, excluded) {
      for (var i = 0; i < ids.length; i++) if (excluded.indexOf(ids[i]) === -1) return ids[i];
      return null;
    };
    var qty = Math.max(1, needQty);
    var line = {
      variantId: '__projected__', productId: '__projected__', collectionIds: [],
      priceCents: Math.ceil(needCents / qty), qty: qty
    };

    var cols = {}, i, j;
    for (i = 0; i < (contextLines || []).length; i++) {
      var lc = contextLines[i].collectionIds || [];
      for (j = 0; j < lc.length; j++) {
        if (scope.excludeCollectionIds.indexOf(lc[j]) === -1) cols[lc[j]] = true;
      }
    }

    if (scope.mode === 'collections') {
      var col = pick(scope.collectionIds, scope.excludeCollectionIds);
      if (col) cols[col] = true;
    } else if (scope.mode === 'products') {
      var prod = pick(scope.productIds, scope.excludeProductIds);
      if (prod) line.productId = prod;
    } else if (scope.mode === 'variants') {
      var v = pick(scope.variantIds, []);
      if (v) line.variantId = v;
    }
    line.collectionIds = Object.keys(cols);
    return line;
  }

  // ─── customer-facing copy

  function fill(template, vars) {
    return String(template || '').replace(/\{(\w+)\}/g, function (m, k) {
      return Object.prototype.hasOwnProperty.call(vars, k) ? String(vars[k]) : m;
    });
  }

  /**
   * "You've unlocked 10% OFF / Add 350 EGP more to unlock 15% OFF", in the merchant's own words.
   * `formatMoney` is supplied by the section so the two panels agree on currency formatting.
   */
  function progressText(summary, messages, formatMoney) {
    messages = messages || {};
    formatMoney = formatMoney || function (c) { return String(c / 100); };
    var out = { unlocked: '', next: '', percentToNext: 0 };

    if (summary.savingsCents > 0 || summary.orderSavingsCents > 0) {
      out.unlocked = fill(messages.unlocked || "You've unlocked {discount}", {
        discount: valueLabel(summary, formatMoney),
        amount: formatMoney(summary.savingsCents),
        tier: summary.tier && summary.tier.label ? summary.tier.label : ''
      });
    }

    var n = summary.next;
    if (n) {
      var key = n.remainingCents > 0 ? 'next' : 'nextQty';
      out.next = fill(messages[key] || (n.remainingCents > 0
        ? 'Add {amount} more to unlock {discount}'
        : 'Add {qty} more item(s) to unlock {discount}'), {
        amount: formatMoney(n.remainingCents),
        qty: n.remainingQty,
        discount: tierValueLabel(n.tier, formatMoney),
        tier: n.tier.label || ''
      });
      if (n.tier.minSpendCents > 0) {
        var have = Math.max(0, n.tier.minSpendCents - n.remainingCents);
        out.percentToNext = Math.max(0, Math.min(100, have / n.tier.minSpendCents * 100));
      } else if (n.tier.minQty > 0) {
        var haveQty = Math.max(0, n.tier.minQty - n.remainingQty);
        out.percentToNext = Math.max(0, Math.min(100, haveQty / n.tier.minQty * 100));
      }
    } else if (summary.savingsCents > 0) {
      // nothing further to work towards on this ladder; only claim the top tier when it IS the top
      if (summary.atTop) out.next = fill(messages.top || '', { discount: valueLabel(summary, formatMoney) });
      out.percentToNext = 100;
    }
    return out;
  }

  function tierValueLabel(tier, formatMoney) {
    return tier.value.type === 'percentage' ? tier.value.percent + '%' : formatMoney(tier.value.amountCents);
  }

  function valueLabel(summary, formatMoney) {
    if (summary.tier) return tierValueLabel(summary.tier, formatMoney);
    return formatMoney(summary.savingsCents);
  }

  // ─── exports

  return {
    OWNER_MARKER: OWNER_MARKER,
    MAX_PCT: MAX_PCT,
    RULE_TYPES: RULE_TYPES,

    // normalisation
    normaliseRule: normaliseRule,
    normaliseRules: normaliseRules,
    normaliseScope: normaliseScope,
    idList: idList,
    parseDate: parseDate,

    // validation
    validateRule: validateRule,
    validateRules: validateRules,
    errorsOnly: errorsOnly,

    // identity
    tierSignature: tierSignature,
    ruleSignature: ruleSignature,
    configSignature: configSignature,
    scopeSignature: scopeSignature,

    // evaluation
    lineInScope: lineInScope,
    savingsOn: savingsOn,
    inSchedule: inSchedule,
    advertisable: advertisable,
    evaluate: evaluate,
    nextTier: nextTier,
    progressText: progressText
  };
});
